import React, { FC, useEffect, useState } from "react";
import { languageType } from "../../TerminalModes/Normal/NormalMode/terminalModeShared";
import { PrivilegeKind } from "../../../../types/Mis/MisTypes";
import { useHidBarcodeScan } from "../../../../hooks/TerminalServices/useHidBarcodeScan";
import classes from "./TerminalMisScanModal.module.scss";

export type TerminalMisScanTarget = {
  privilegeTypeId?: number;
  misCode?: string | null;
  privilegeKind: PrivilegeKind;
  name: string;
  scanCard?: boolean;
  scanOnService?: boolean;
  topicId?: number;
};

type TerminalMisScanModalProps = {
  language: languageType;
  target: TerminalMisScanTarget;
  allowsDigitalCode: boolean;
  allowsBarcode: boolean;
  localTest?: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onSubmit: (value: string) => void;
  onSkipBenefit?: () => void;
};

const copy = {
  benefitTitle: {
    ru: "Приложите карту пациента",
    by: "Прыкладзіце карту пацыента",
    en: "Scan the patient card",
  },
  otoTitle: {
    ru: "Экстренный приём (CITO)",
    by: "Экстрэнны прыём (CITO)",
    en: "Emergency (CITO)",
  },
  benefitHint: {
    ru: "Отсканируйте штрихкод. Печать талона возможна только после подтверждения МИС.",
    by: "Адскануйце штрыхкод. Друк талона магчымы толькі пасля пацверджання МІС.",
    en: "Scan the barcode. A ticket can be printed only after MIS confirmation.",
  },
  benefitHintLocal: {
    ru: "Отсканируйте штрихкод или введите номер карты. Печать талона возможна только после подтверждения МИС.",
    by: "Адскануйце штрыхкод або ўвядзіце нумар карты. Друк талона магчымы толькі пасля пацверджання МІС.",
    en: "Scan the barcode or enter the card number. A ticket can be printed only after MIS confirmation.",
  },
  cardPlaceholder: {
    ru: "Номер карты",
    by: "Нумар карты",
    en: "Card number",
  },
  otoHint: {
    ru: "Введите цифровой код или отсканируйте штрихкод.",
    by: "Увядзіце лічбавы код або адскануйце штрыхкод.",
    en: "Enter the digital code or scan a barcode.",
  },
  otoCodeOnly: {
    ru: "Введите цифровой код.",
    by: "Увядзіце лічбавы код.",
    en: "Enter the digital code.",
  },
  otoBarcodeOnly: {
    ru: "Отсканируйте штрихкод.",
    by: "Адскануйце штрыхкод.",
    en: "Scan the barcode.",
  },
  cancel: { ru: "Отмена", by: "Адмена", en: "Cancel" },
  submit: { ru: "Подтвердить", by: "Пацвердзіць", en: "Confirm" },
  skip: { ru: "Без льгот", by: "Без льгот", en: "Without benefits" },
  scanOnServiceHint: {
    ru: "Отсканируйте медкарту, чтобы получить льготный талон. Или получите обычный талон без льготы.",
    by: "Адскануйце медкарту, каб атрымаць льготны талон. Або атрымайце звычайны талон без льготы.",
    en: "Scan the medical card for a preferential ticket, or continue without benefits.",
  },
};

export const TerminalMisScanModal: FC<TerminalMisScanModalProps> = ({
  language,
  target,
  allowsDigitalCode,
  allowsBarcode,
  localTest = false,
  isSubmitting,
  errorMessage,
  onCancel,
  onSubmit,
  onSkipBenefit,
}) => {
  const [code, setCode] = useState("");
  const isEmergency = target.privilegeKind === "EMERGENCY";
  const showKeypad = isEmergency && allowsDigitalCode;
  const showManualInput =
    localTest && (!isEmergency || (allowsBarcode && !showKeypad));
  const isScanOnService = Boolean(target.scanOnService);

  useHidBarcodeScan({
    enabled: allowsBarcode || !isEmergency,
    onScan: (value) => {
      if (isSubmitting) return;
      onSubmit(value);
    },
  });

  useEffect(() => {
    setCode("");
  }, [target.privilegeTypeId, target.scanCard, target.topicId]);

  const title = isEmergency
    ? copy.otoTitle[language]
    : copy.benefitTitle[language];
  const hint = isScanOnService
    ? copy.scanOnServiceHint[language]
    : !isEmergency
      ? (localTest ? copy.benefitHintLocal : copy.benefitHint)[language]
      : allowsDigitalCode && allowsBarcode
        ? copy.otoHint[language]
        : allowsDigitalCode
          ? copy.otoCodeOnly[language]
          : copy.otoBarcodeOnly[language];

  const appendDigit = (digit: string) => {
    setCode((prev) => `${prev}${digit}`.slice(0, 32));
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true">
      <div className={classes.card}>
        <div className={classes.title}>{title}</div>
        <div className={classes.hint}>
          {target.name}. {hint}
        </div>
        {errorMessage && <div className={classes.error}>{errorMessage}</div>}
        {showManualInput && (
          <input
            className={classes.cardInput}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            disabled={isSubmitting}
            placeholder={copy.cardPlaceholder[language]}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) {
                e.preventDefault();
                onSubmit(code.trim());
              }
            }}
          />
        )}
        {showKeypad && (
          <>
            <div className={classes.code} aria-hidden="true">
              {code ? "*".repeat(code.length) : ""}
            </div>
            <div className={classes.keypad}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"].map(
                (key) => (
                  <button
                    key={key}
                    type="button"
                    className={classes.key}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (key === "⌫") {
                        setCode((prev) => prev.slice(0, -1));
                        return;
                      }
                      if (key === "OK") {
                        if (code.trim()) onSubmit(code.trim());
                        return;
                      }
                      appendDigit(key);
                    }}
                  >
                    {key}
                  </button>
                ),
              )}
            </div>
          </>
        )}
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.secondary}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {copy.cancel[language]}
          </button>
          {isScanOnService && onSkipBenefit && (
            <button
              type="button"
              className={classes.skip}
              onClick={onSkipBenefit}
              disabled={isSubmitting}
            >
              {copy.skip[language]}
            </button>
          )}
          {(showKeypad || showManualInput) && (
            <button
              type="button"
              className={classes.primary}
              disabled={isSubmitting || !code.trim()}
              onClick={() => onSubmit(code.trim())}
            >
              {copy.submit[language]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminalMisScanModal;
